import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { KpiDocsExportParams } from '../../../services/queries/KpiPeriodRequestDocumentQueries';
import { useKpiDocsExport } from '../../../services/queries-hooks/KpiPeriodRequestDocumentQueriesHooks';

/**
 * Stateless export button for the Manage Requests page. Each click kicks off
 * a fresh bulk export using the current filter props. Shows a spinner while
 * polling; auto-opens the ZIP download when the job completes.
 */
export function KpiDocsExportButton(params: KpiDocsExportParams) {
  const { exportState, initiateExport, reset } = useKpiDocsExport(params);
  const { pushSuccessToast, pushErrorToast, pushInfoToast } = useToastMessageState();

  const isRunning = exportState.phase === 'initiating' || exportState.phase === 'polling';

  useEffect(() => {
    if (exportState.phase === 'complete') {
      window.open(exportState.downloadUrl, '_blank', 'noopener,noreferrer');
      pushSuccessToast({ message: 'KPI docs export ready — download started.' });
      reset();
    } else if (exportState.phase === 'empty') {
      pushInfoToast({ message: 'No KPI documents found for the selected filters.' });
      reset();
    } else if (exportState.phase === 'failed' || exportState.phase === 'error') {
      const message =
        exportState.phase === 'failed'
          ? (exportState.errorMessage ?? 'Export failed. Please try again.')
          : exportState.message;
      pushErrorToast({ message });
      reset();
    }
  }, [exportState, pushSuccessToast, pushErrorToast, pushInfoToast, reset]);

  return (
    <Button
      variant='outlined'
      color='secondary'
      size='small'
      startIcon={isRunning ? <CircularProgress size={14} color='inherit' /> : <DownloadIcon />}
      onClick={initiateExport}
      disabled={isRunning}
    >
      Export KPI Docs
    </Button>
  );
}
